/**
 * @module support/shared/widgets
 * @copyright 1994-2025 T-Mobile US, Inc. All Rights Reserved.
 */
/** */
import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  Output,
  Provider,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { SelectItem } from 'primeng/api';
import { DeviceDetectionService } from '@support/shared/widgets/services/device-detection.service';

/* tslint:disable:no-forward-ref */

/**
 * Provider Expression that allows tmo-checkbox to register as a ControlValueAccessor.
 * This allows it to support [(ngModel)].
 * @docs-private
 */
export const DROPDOWN_CONTROL_VALUE_ACCESSOR: Provider = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => DropdownComponent),
  multi: true,
};

/**
 * A T-mobile custom design dropdown component. Supports all of the functionality of an HTML5 dropdown,
 * and exposes a similar API. A tmo-dropdown can have search section,scroll bar,placeholder,first item
 * default focused. Note that all additional accessibility attributes are taken care of by the component,
 * so there is no need to provide them yourself..
 */
@Component({
  selector: 'tmo-dropdown',
  templateUrl: './dropdown.component.html',
  styleUrls: ['../../../../../../style-sheets/components/dropdown.component.scss'],
  providers: [DROPDOWN_CONTROL_VALUE_ACCESSOR],
})
export class DropdownComponent implements ControlValueAccessor, OnDestroy {
  /** Event emitted when the dropdown `selected` value changes.
   * It emits the 'Event' object.
   */
  @Output() public selectionChange: EventEmitter<Object>;
  /** Event emitted when the dropdown `onFocus` value changes.
   * It emits the 'Event' object.
   */
  @Output() public selectionFocus: EventEmitter<Object>;
  /** Event emitted when the dropdown `onBlur` value changes.
   * It emits the 'Event' object.
   */
  @Output() public selectionBlur: EventEmitter<Object>;
  /**
   * SelectItem array input property to show the dropdown list.
   */
  @Input() public options: SelectItem[];
  /**
   * Boolean input property to show the search section in dropdown.
   */
  @Input() public filter: boolean;
  /**
   * Used to show Icon in dropdown options
   */
  @Input() public showIcon: boolean;
  /**
   * Boolean input property to disable the search section in dropdown.
   */
  @Input() public disabled: boolean;
  /**
   * String input property to show the placeholder in dropdown.
   */
  @Input() public placeholder: string;
  /**
   * String input property to show the scroll bar in dropdown list.
   */
  @Input() public scrollHeight: string;
  /**
   * Boolean input property to make the first item of the dropdown list auto focused.
   */
  @Input() public focusFirstElement: boolean;
  /**
   * Boolean input property to make the first item of the dropdown list auto focused.
   */
  @Input() public validationError: boolean;
  /**
   * Boolean input property to  enable functionality for making few items disabled in dropdown
   */
  @Input() public disableItems?: boolean;

  /**
   * A unique vaule to be assigned as an id of the focus input to match a label defined for the component.
   */
  @Input() public id?: string;

  @Input() public prefix?: string;

  /** Internal property to store current state of input box. */
  private innerValue: object;
  /**
   * variable to check mobile device
   */
  public isMobileDevice: boolean;

  public isTablet: boolean;
  /**
   * variable to hold error message
   */
  @Input() public validationErrorMsg: string;
  /**
   * Boolean to keep track if element is in focus
   */
  public isFocused: boolean;

  /**
   * Whenever the dropdown component needs some value to be instantiated on load of this component
   * values of its properties are initialized in the constructor
   */
  constructor(private deviceDetectionService: DeviceDetectionService) {
    this.selectionChange = new EventEmitter();
    this.selectionFocus = new EventEmitter();
    this.selectionBlur = new EventEmitter();
    this.innerValue = null;
    this.isMobileDevice = this.deviceDetectionService.isMobile;
    this.isTablet = this.deviceDetectionService.isTablet;
  }

  /**
   * Called whenever a item is selected from dropdown
   * @param event - Event object
   * Emits an event with 'event' object as data.
   * @TODO TYPE?
   */
  public onSelectChange(event: { originalEvent?: { path: string } }): void {
    const value = 'value';
    this.selectionChange.emit({
      val: event[value],
    });
    // tslint:disable-next-line:no-global-variables
    window.dispatchEvent(
      new CustomEvent('change', {
        detail: { path: event.originalEvent.path, type: 'change' },
      })
    );
  }

  /**
   * Called whenever a item is selected from dropdown
   * @param event - Event object
   * Emits an event with 'event' object as data.
   * @TODO TYPE?
   */
  // tslint:disable no-any
  public newonSelectChange(event: Event, options: any, index: number): void {
    const path = 'path';
    let value = options ? options.value : event;
    if (this.placeholder) {
      index = index - 1;
      value = this.options[index] && this.options[index].value ? this.options[index].value : event;
    }
    this.selectionChange.emit({
      val: value,
    });
    // tslint:disable-next-line:no-global-variables
    window.dispatchEvent(
      new CustomEvent('change', {
        detail: { path: event[path], type: 'change' },
      })
    );
  }

  /**
   * Called whenever the dropdown is clicked
   * @param event - Event object
   * Emits an event with 'event' object as data.
   * @TODO TYPE?
   */
  public onFocus(event: Object): void {
    this.isFocused = true;
    const value = 'value';
    this.selectionFocus.emit({
      val: event[value],
    });
  }

  /**
   * ngModel accessor to get the value of property [based on input type]
   * @docs-private
   */
  get value(): {} {
    return this.innerValue || this.placeholder || (this.options && this.options[0].value);
  }

  /**
   * ngModel accessor to set the value
   * @param value - {} value: could be number or string
   * @docs - private
   */
  set value(value: {}) {
    if (value !== this.innerValue) {
      this.innerValue = value;
      this.onChangeCallback(value);
    }
  }

  /**
   * Called whenever the input is focused out
   * @returns void
   */
  public onBlur(): void {
    this.isFocused = false;
    this.onTouchedCallback();
  }

  /**
   * Sets the model value. Implemented as part of ControlValueAccessor.
   * @param value Value to be set to the model.
   * @returns void
   */
  public writeValue(value: Object): void {
    if (value !== this.innerValue) {
      this.innerValue = value;
      this.onChangeCallback(this.innerValue);
    }
  }

  /**
   * Registers a callback to be triggered when the value has changed.
   * Implemented as part of ControlValueAccessor.
   * @param fn Function to be called on change.
   */
  public registerOnChange(fn: (_: boolean) => {}): void {
    this.onChangeCallback = fn;
  }

  /**
   * Registers a callback to be triggered when the control has been touched.
   * Implemented as part of ControlValueAccessor.
   * @param fn Callback to be triggered when the input is touched.
   * @returns void
   */
  public registerOnTouched(fn: () => {}): void {
    this.onTouchedCallback = fn;
  }

  /**
   * Destroys the subscriptions.
   * @returns void
   */
  public ngOnDestroy(): void {}

  /**
   * Called when the input is blurred. Needed to properly implement ControlValueAccessor.
   * @docs-private
   * @returns void
   */
  public onTouchedCallback(): void {}

  /**
   * Called when the input value is changed. Needed to properly implement ControlValueAccessor.
   * @docs-private
   * @returns void
   */
  public onChangeCallback(_: {}): void {}

  /**
   * Called on click of a disabled selection item
   * @docs-private
   * @returns void
   */
  public onClick(disabled: boolean, event: Event): void {
    if (disabled) {
      event.stopPropagation();
    }
  }

  /**
   * To get the placeholder name
   * @docs-private
   * @returns string
   */
  public getPlaceholder(value, placeholder): string {
    return value?.name ? value?.name : value || placeholder;
  }  
}
